/**
 * Compact P&L / status strip for the signal chart pane — the shared read-only
 * bot_all reference. Sits at the top-right of the signal pane (clear of the
 * chart's own OHLC legend at top-left), showing at a glance: source live/offline,
 * account equity, floating P&L, and open-position count. Read-only; no actions.
 */
import { useAppSelector } from "@/state/hooks";

function fmtMoney(n: number | undefined, currency: string): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

export function SignalPnlStrip() {
  const online = useAppSelector((s) => s.signal.online);
  const connected = useAppSelector((s) => s.signal.connected);
  const account = useAppSelector((s) => s.signal.account);
  const positions = useAppSelector((s) => s.signal.positions);
  const symbol = useAppSelector((s) => s.signal.symbol);

  const currency = account?.currency ?? "USD";
  const floating = account?.profit;
  const live = online && connected;

  return (
    <div
      style={{
        position: "absolute",
        top: 6,
        right: 8,
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "4px 9px",
        borderRadius: 6,
        fontSize: 11,
        lineHeight: 1.3,
        fontVariantNumeric: "tabular-nums",
        color: "var(--text-secondary, #c7ccd4)",
        background: "var(--panel-bg, rgba(20,26,38,0.82))",
        border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
        backdropFilter: "blur(2px)",
        pointerEvents: "none",
        userSelect: "none",
      }}
      aria-label="Signal chart status"
    >
      <span style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 600 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: live ? "#22c55e" : "#f59e0b",
            boxShadow: live ? "0 0 5px #22c55e" : "none",
          }}
        />
        SIGNAL · {symbol}
      </span>
      {live ? (
        <>
          <span>
            Equity <strong style={{ color: "var(--text-primary, #e8eaed)" }}>
              {fmtMoney(account?.equity, currency)}
            </strong>
          </span>
          <span>
            P&L{" "}
            <strong
              style={{
                color:
                  floating == null
                    ? "inherit"
                    : floating >= 0
                      ? "#22c55e"
                      : "#ef4444",
              }}
            >
              {floating == null
                ? "—"
                : `${floating >= 0 ? "+" : ""}${fmtMoney(floating, currency)}`}
            </strong>
          </span>
          <span>
            Open <strong style={{ color: "var(--text-primary, #e8eaed)" }}>
              {positions.length}
            </strong>
          </span>
        </>
      ) : (
        <span style={{ color: "#f59e0b" }}>source offline</span>
      )}
    </div>
  );
}
